// SPDX-FileCopyrightText: 2025 Slant Tech LLC
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * DiffControls - Toggle and summary for diff view mode
 *
 * Provides:
 * - Toggle button to enable/disable diff view
 * - Summary of changes (added, removed, modified counts)
 * - Legend showing color meanings
 */
function DiffControls({
  diffEnabled,
  onToggle,
  diffData,
  isLoading = false,
  error = null
}) {
  const summary = diffData?.diff?.summary || {
    added_count: 0,
    removed_count: 0,
    modified_count: 0
  };

  const totalChanges = summary.added_count + summary.removed_count + summary.modified_count;
  const hasChanges = totalChanges > 0;

  return (
    <div className="flex items-center gap-3">
      {/* Diff Toggle Button */}
      <button
        onClick={onToggle}
        disabled={isLoading}
        className={`
          flex items-center gap-2 px-3 py-1.5 text-sm font-display font-semibold
          transition-all duration-200
          ${diffEnabled
            ? 'bg-st-accent text-white shadow-lg'
            : 'bg-st-tertiary text-st-muted hover:bg-st-border-subtle hover:text-st-text'
          }
          ${isLoading ? 'opacity-50 cursor-wait' : ''}
        `}
        title={diffEnabled ? 'Hide changes' : 'Show changes between branches'}
      >
        {isLoading ? (
          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        )}
        <span>Diff View</span>

        {/* Change count badge */}
        {hasChanges && !isLoading && (
          <span className={`
            px-1.5 py-0.5 text-xs font-mono font-bold
            ${diffEnabled ? 'bg-white/20' : 'bg-st-accent text-white'}
          `}>
            {totalChanges}
          </span>
        )}
      </button>

      {/* Summary and Legend (shown when diff is enabled) */}
      {diffEnabled && !isLoading && !error && (
        <div className="flex items-center gap-4 text-xs font-mono">
          {/* Added count */}
          {summary.added_count > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 bg-st-success" />
              <span className="text-st-success font-semibold">
                +{summary.added_count} added
              </span>
            </div>
          )}

          {/* Removed count */}
          {summary.removed_count > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 bg-st-error" />
              <span className="text-st-error font-semibold">
                −{summary.removed_count} removed
              </span>
            </div>
          )}

          {/* Modified count */}
          {summary.modified_count > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 bg-st-warning" />
              <span className="text-st-warning font-semibold">
                ~{summary.modified_count} modified
              </span>
            </div>
          )}

          {/* No changes message */}
          {!hasChanges && (
            <span className="text-st-muted italic font-body">No changes detected</span>
          )}
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="flex items-center gap-2 text-xs text-st-error font-body">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Failed to load diff</span>
        </div>
      )}
    </div>
  );
}

/**
 * DiffLegend - Standalone legend component for showing in other locations
 */
export function DiffLegend({ className = '' }) {
  return (
    <div className={`flex items-center gap-4 text-xs font-mono ${className}`}>
      <div className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 bg-st-success" />
        <span className="text-st-muted">Added</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 bg-st-error" />
        <span className="text-st-muted">Removed</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 bg-st-warning" />
        <span className="text-st-muted">Modified</span>
      </div>
    </div>
  );
}

/**
 * DiffBranchInfo - Shows source and target branch info
 */
export function DiffBranchInfo({ diffData, className = '' }) {
  if (!diffData) return null;

  const { source_branch, target_branch, is_new_file, is_deleted_file } = diffData;

  return (
    <div className={`flex items-center gap-2 text-xs ${className}`}>
      <span className="text-st-muted font-body">Comparing:</span>
      <code className="px-1.5 py-0.5 bg-st-success/20 text-st-success font-mono">
        {source_branch}
      </code>
      <svg className="w-4 h-4 text-st-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
      </svg>
      <code className="px-1.5 py-0.5 bg-st-tertiary text-st-muted font-mono">
        {target_branch}
      </code>

      {is_new_file && (
        <span className="px-1.5 py-0.5 bg-st-success/20 text-st-success text-xs font-mono font-semibold uppercase">
          New file
        </span>
      )}
      {is_deleted_file && (
        <span className="px-1.5 py-0.5 bg-st-error/20 text-st-error text-xs font-mono font-semibold uppercase">
          Deleted file
        </span>
      )}
    </div>
  );
}

export default DiffControls;
